import React, { useState } from "react";
import { Sparkles, Info, Loader2, Check, PlusCircle, Layers, Save, CheckCircle2, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { historyService } from "@/services/historyService";
import { errorBankService } from "@/services/errorBankService";
import { flashcardService } from "@/services/flashcardService";
import { getCurrentUserIdOrThrow } from "@/services/authService";

interface WritingV2CorrectionPanelProps {
  task: any;
  correction: any;
  historyRecordId?: string | null;
  fromHistory?: boolean;
  testMode?: "complete" | "individual";
}

function getWritingErrorKey(error: any, index?: number): string {
  return error.id || `${error.original}-${error.corrected}-${index || ""}`;
}

export const WritingV2CorrectionPanel: React.FC<WritingV2CorrectionPanelProps> = ({
  task,
  correction,
  historyRecordId: propHistoryRecordId,
  fromHistory = false,
  testMode = "individual",
}) => {
  const section = task.section || "Section A";

  // States for actions
  const [localHistoryRecordId, setLocalHistoryRecordId] = useState<string | null>(propHistoryRecordId || null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");
  const [isSaving, setIsSaving] = useState(false);
  
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [sendResult, setSendResult] = useState<{
    status: "success" | "duplicate" | "partial";
    message: string;
    insertedCount: number;
    duplicatedCount?: number;
  } | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const [isCreatingAllFlashcards, setIsCreatingAllFlashcards] = useState(false);
  const [isCreatingFlashcard, setIsCreatingFlashcard] = useState<Record<string, boolean>>({});
  const [createdFlashcardsByErrorId, setCreatedFlashcardsByErrorId] = useState<Record<string, boolean>>({});

  // Calculate severity counts
  const severityCounts = {
    high: correction.errors.filter((e: any) => e.severity === "high").length,
    medium: correction.errors.filter((e: any) => e.severity === "medium").length,
    low: correction.errors.filter((e: any) => e.severity === "low").length,
  };

  // Sort errors by severity
  const severityRank: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const sortedErrors = [...correction.errors].sort(
    (a: any, b: any) => severityRank[a.severity] - severityRank[b.severity]
  );

  const effectiveHistoryRecordId = localHistoryRecordId || propHistoryRecordId || null;

  // Handlers
  const handleSaveHistory = async () => {
    try {
      setIsSaving(true);
      setSaveStatus("idle");

      console.log("[EE_V2_HISTORY_BUTTON_CLICK]", {
        section: task?.section,
        hasTask: Boolean(task),
        hasCorrection: Boolean(correction),
        userTextLength: task?.userText?.length || 0,
        testMode,
      });

      const saved = await historyService.saveWritingV2CorrectionToHistory({
        task,
        correction,
        testMode: testMode === "individual" ? "single" : "complete",
      });

      if (!saved?.id) {
        throw new Error("Histórico não retornou registro salvo.");
      }

      setLocalHistoryRecordId(saved.id);
      setSaveStatus("success");

    } catch (error: any) {
      console.error("[EE_V2_HISTORY_BUTTON_FAILED]", error);
      setSaveStatus("error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendToErrorBank = async () => {
    try {
      setIsSending(true);
      setSendError(null);
      setSendResult(null);

      console.log("[EE_V2_ACTION_PANEL_SEND_ERRORS_TO_BANK_CLICK]", {
        task,
        correction,
        historyRecordId: effectiveHistoryRecordId,
        section
      });

      const result = await errorBankService.saveWritingV2ErrorsToErrorBank({
        task,
        correction,
        historyRecordId: effectiveHistoryRecordId,
      });

      setSendResult(result);
      setIsSent(true);
    } catch (error: any) {
      console.error("[EE_V2_ERROR_BANK_FAILED]", error);
      setSendError("Não foi possível enviar os erros ao Banco de Erros. Tente novamente.");
    } finally {
      setIsSending(false);
    }
  };

  const handleCreateSingleFlashcard = async (error: any) => {
    const errorKey = getWritingErrorKey(error);
    try {
      setIsCreatingFlashcard(prev => ({ ...prev, [errorKey]: true }));

      console.log("[EE_V2_ACTION_PANEL_SINGLE_FLASHCARD_CLICK]", {
        errorId: error.id,
        section
      });

      await flashcardService.createFlashcardsFromWritingV2Errors({
        task,
        errors: [error],
        historyRecordId: effectiveHistoryRecordId,
      });

      setCreatedFlashcardsByErrorId(prev => ({ ...prev, [errorKey]: true }));
    } catch (error: any) {
      console.error("[EE_V2_CREATE_FLASHCARD_FAILED]", error);
    } finally {
      setIsCreatingFlashcard(prev => ({ ...prev, [errorKey]: false }));
    }
  };

  const handleCreateAllFlashcards = async () => {
    try {
      setIsCreatingAllFlashcards(true);

      console.log("[EE_V2_ACTION_PANEL_ALL_FLASHCARDS_CLICK]", { section });

      await flashcardService.createFlashcardsFromWritingV2Errors({
        task,
        errors: correction.errors,
        historyRecordId: effectiveHistoryRecordId,
      });

      // Mark all as created
      const newCreated: Record<string, boolean> = {};
      correction.errors.forEach((err: any) => {
        newCreated[getWritingErrorKey(err)] = true;
      });
      setCreatedFlashcardsByErrorId(newCreated);
    } catch (error: any) {
      console.error("[EE_V2_CREATE_ALL_FLASHCARDS_FAILED]", error);
    } finally {
      setIsCreatingAllFlashcards(false);
    }
  };

  console.log("[EE_V2_ACTION_PANEL_RENDER]", {
    section,
    testMode,
    fromHistory,
    hasCorrection: Boolean(correction),
    errorCount: correction?.errors?.length || 0,
  });

  console.log("[EE_V2_CORRECTION_UI_RENDER_ACTIVE]", {
    component: "WritingV2CorrectionPanel",
    section: task?.section || correction?.section,
    fromHistory,
    hasCorrection: Boolean(correction),
  });

  return (
    <div className="space-y-6 mt-6 border-t border-gray-100 pt-6">
      <div className="flex items-center gap-2 text-purple-700">
        <Sparkles className="w-5 h-5" />
        <div className="flex flex-col gap-0.5">
          <h2 className="text-lg font-bold">Correction pédagogique — {section}</h2>
          <p className="text-xs text-gray-500">Esta análise identifica erros linguísticos e sugestões de estudo. Ela não representa uma correção oficial do TEF Canada.</p>
        </div>
      </div>

      {/* Resumo da Correção Pedagógica */}
      <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-4">
        <h3 className="text-sm font-bold text-gray-900">Resumo da correção</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg text-center">
            <p className="text-xs text-gray-500 font-medium">Erros identificados</p>
            <p className="text-lg font-bold text-gray-900">{correction.errors.length}</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg text-center">
            <p className="text-xs text-gray-500 font-medium">Palavras analisadas</p>
            <p className="text-lg font-bold text-gray-900">{correction.wordCount} / {section === "Section B" ? "200" : "80"}</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg text-center flex flex-col justify-center items-center">
            <p className="text-xs text-gray-500 font-medium">Mínimo de palavras</p>
            <Badge variant={correction.meetsMinimumWordCount ? "default" : "destructive"} className="mt-1 text-xs">
              {correction.meetsMinimumWordCount ? "Atingido" : "Não atingido"}
            </Badge>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg text-center">
            <p className="text-xs text-gray-500 font-medium">Tipo de análise</p>
            <p className="text-sm font-bold text-gray-900 mt-1">Pedagógica</p>
          </div>
        </div>

        <div>
          <p className="text-xs text-gray-500 font-medium mb-1">Principais focos de estudo:</p>
          <div className="flex flex-wrap gap-1.5">
            {correction.mainFocusAreas.slice(0, 5).map((area: string, idx: number) => (
              <Badge key={idx} variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-100">
                {area}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Aviso Pedagógico */}


      {/* Feedback Geral */}
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-2">Feedback Geral</h3>
        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <p className="text-gray-800 text-xs leading-relaxed whitespace-pre-wrap">{correction.generalFeedback}</p>
        </div>
      </div>

      {/* Erros Identificados */}
      <div className="space-y-3">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-bold text-gray-900">Erros Identificados</h3>
          <p className="text-xs text-gray-500">Revise os pontos abaixo para entender seus desvios recorrentes.</p>
        </div>

        {sortedErrors.length === 0 ? (
          <p className="text-gray-500 text-xs">Nenhum erro encontrado! Excelente trabalho.</p>
        ) : (
          sortedErrors.map((error: any, idx: number) => {
            let context = error.contextSentence || "";
            if (context.length > 300) {
              context = context.slice(0, 300) + "...";
            }

            const errorKey = getWritingErrorKey(error);

            return (
              <div key={error.id || idx} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-gray-50">
                      {error.category}
                    </Badge>
                    <Badge 
                      variant={error.severity === "high" ? "destructive" : error.severity === "medium" ? "default" : "secondary"} 
                      className={`text-xs ${
                        error.severity === "high" 
                          ? "bg-red-50 text-red-700 hover:bg-red-50" 
                          : error.severity === "medium" 
                            ? "bg-amber-50 text-amber-700 hover:bg-amber-50" 
                            : "bg-blue-50 text-blue-700 hover:bg-blue-50"
                      }`}
                    >
                      {error.severity === "high" ? "Alta" : error.severity === "medium" ? "Média" : "Baixa"}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-2 bg-red-50/50 border border-red-100 rounded">
                    <p className="text-xs font-bold text-red-700 mb-0.5">Original</p>
                    <p className="text-xs text-gray-800">{error.original}</p>
                  </div>
                  <div className="p-2 bg-green-50/50 border border-green-100 rounded">
                    <p className="text-xs font-bold text-green-700 mb-0.5">Corrigido</p>
                    <p className="text-xs text-gray-800">{error.corrected}</p>
                  </div>
                </div>

                {context && (
                  <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                    <p className="font-bold mb-0.5">Frase de contexto</p>
                    <p className="italic">"...{context}..."</p>
                  </div>
                )}

                <div className="text-xs text-gray-700">
                  <p className="font-bold mb-0.5">Explicação</p>
                  <p>{error.explanation}</p>
                </div>

                {error.flashcardSuggestion && (
                  <div className="text-xs text-purple-700 bg-purple-50 p-2 rounded border border-purple-100">
                    <p className="font-bold mb-0.5">Sugestão de Flashcard</p>
                    <p><span className="font-medium">Frente:</span> {error.flashcardSuggestion.front}</p>
                    <p><span className="font-medium">Verso:</span> {error.flashcardSuggestion.back}</p>
                  </div>
                )}

                <div className="flex justify-end mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleCreateSingleFlashcard(error)}
                    disabled={
                      isCreatingFlashcard[errorKey] ||
                      createdFlashcardsByErrorId[errorKey]
                    }
                    className={`text-xs h-8 ${
                      createdFlashcardsByErrorId[errorKey]
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        : "text-purple-700 border-purple-200 hover:bg-purple-50"
                    }`}
                  >
                    {isCreatingFlashcard[errorKey] ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        Criando...
                      </>
                    ) : createdFlashcardsByErrorId[errorKey] ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Flashcard criado
                      </>
                    ) : (
                      <>
                        <PlusCircle className="w-3 h-3 mr-1" />
                        Criar flashcard
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Versão Corrigida */}
      {correction.correctedVersion && (
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-2">Versão Corrigida do Texto</h3>
          <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <p className="text-xs text-gray-500 mb-2">Versão sugerida com ajustes linguísticos, preservando as ideias principais do texto original.</p>
            <p className="text-gray-800 text-xs whitespace-pre-wrap leading-relaxed">{correction.correctedVersion}</p>
          </div>
        </div>
      )}

      {/* Sugestões de Estudo */}
      {correction.studySuggestions && correction.studySuggestions.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-900 mb-2">Sugestões de Estudo</h3>
          <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <ul className="list-decimal list-inside text-gray-700 text-xs space-y-1">
              {correction.studySuggestions.map((suggestion: string, idx: number) => (
                <li key={idx}>{suggestion}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Salvar no Histórico e Enviar para Banco de Erros */}
      <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-3 shadow-sm">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Ações</h3>
          <p className="text-xs text-gray-500">Salve a correção ou envie os erros para o banco.</p>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleSaveHistory}
            disabled={isSaving || Boolean(effectiveHistoryRecordId)}
            className={`w-full text-xs font-semibold rounded-lg h-9 ${
              saveStatus === "success" || Boolean(effectiveHistoryRecordId)
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "bg-purple-600 hover:bg-purple-700 text-white"
            }`}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                Salvando...
              </>
            ) : saveStatus === "success" || Boolean(effectiveHistoryRecordId) ? (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                Salvo no histórico
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5 mr-1" />
                Salvar correção no histórico
              </>
            )}
          </Button>

          <Button
            onClick={handleSendToErrorBank}
            disabled={
              isSending ||
              isSent ||
              !correction?.errors ||
              correction.errors.length === 0
            }
            className={`w-full text-xs font-semibold rounded-lg h-9 ${
              isSent
                ? sendResult?.status === "duplicate"
                  ? "bg-slate-500 hover:bg-slate-600 text-white"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "bg-indigo-600 hover:bg-indigo-700 text-white"
            }`}
          >
            {isSending ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                Enviando...
              </>
            ) : isSent ? (
              sendResult?.status === "duplicate" ? (
                <>
                  <Check className="w-3.5 h-3.5 mr-1" />
                  Erros já enviados
                </>
              ) : sendResult?.status === "partial" ? (
                <>
                  <Check className="w-3.5 h-3.5 mr-1" />
                  Erros enviados (parcial)
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5 mr-1" />
                  Erros enviados
                </>
              )
            ) : !correction?.errors || correction.errors.length === 0 ? (
              <>
                Nenhum erro para enviar
              </>
            ) : (
              <>
                <PlusCircle className="w-3.5 h-3.5 mr-1" />
                Enviar erros ao Banco de Erros
              </>
            )}
          </Button>

          {isSent && sendResult && (
            <p className={`text-xs font-bold mt-1 ${sendResult.status === "duplicate" ? "text-slate-500" : "text-emerald-600"}`}>
              {sendResult.message}
            </p>
          )}

          <Button
            onClick={handleCreateAllFlashcards}
            disabled={
              isCreatingAllFlashcards ||
              !correction?.errors ||
              correction.errors.length === 0 ||
              correction.errors.every((error: any) => createdFlashcardsByErrorId[getWritingErrorKey(error)])
            }
            className={`w-full text-xs font-semibold rounded-lg h-9 ${
              correction?.errors && correction.errors.length > 0 && correction.errors.every((error: any) => createdFlashcardsByErrorId[getWritingErrorKey(error)])
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "bg-purple-600 hover:bg-purple-700 text-white"
            }`}
          >
            {isCreatingAllFlashcards ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                Criando flashcards...
              </>
            ) : correction?.errors && correction.errors.length > 0 && correction.errors.every((error: any) => createdFlashcardsByErrorId[getWritingErrorKey(error)]) ? (
              <>
                <Check className="w-3.5 h-3.5 mr-1" />
                Flashcards criados
              </>
            ) : !correction?.errors || correction.errors.length === 0 ? (
              <>
                Nenhum erro para transformar em flashcard
              </>
            ) : (
              <>
                <Layers className="w-3.5 h-3.5 mr-1" />
                Criar flashcards dos erros
              </>
            )}
          </Button>
 
          {sendError && (
            <p className="text-xs text-red-600 mt-1 font-bold">{sendError}</p>
          )}
        </div>
      </div>
    </div>
  );
};
